/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, ArcElement, Tooltip, type ChartOptions, type Plugin } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, ChartDataLabels);

const datalabelsLinePlugin = {
  id: 'lineConnector',
  afterDatasetsDraw: (chart: any) => {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const datasetMeta = chart.getDatasetMeta(0);
    if (!datasetMeta?.data) return;

    const radius = datasetMeta.data[0]?.outerRadius;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const centerX = (chartArea.left + chartArea.right) / 2;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const breakDistance = radius + 20;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const labelDistance = radius + 90;

    ctx.save();

    datasetMeta.data.forEach((arc: any) => {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const angle = (arc.startAngle + arc.endAngle) / 2;

      const startX = centerX + Math.cos(angle) * radius;
      const startY = centerY + Math.sin(angle) * radius;

      const breakX = centerX + Math.cos(angle) * breakDistance;
      const breakY = centerY + Math.sin(angle) * breakDistance;

      const endX = centerX + Math.cos(angle) * labelDistance;
      const endY = breakY + Math.sin(angle) * 13;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(breakX, breakY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = '#989898';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(endX, endY, 2, 0, Math.PI * 2);
      ctx.fillStyle = '#989898';
      ctx.fill();
    });

    ctx.restore();
  },
};

interface EventoFavorito {
  eventId: number;
  name: string;
  count: number;
  percentage: number;
}

interface GraficoEventosFavoritosProps {
  eventosFavoritos?: {
    eventos: EventoFavorito[];
    total: number;
  };
  loading?: boolean;
  style?: React.CSSProperties; // ✅ novo
}

const GraficoEventosFavoritos: React.FC<GraficoEventosFavoritosProps> = ({
  eventosFavoritos,
  loading = false,
  style,
}) => {
  const [isLoading, setIsLoading] = useState(loading);

  const plugins: Array<Plugin<'doughnut'>> = [
    ChartDataLabels as Plugin<'doughnut'>,
    datalabelsLinePlugin as Plugin<'doughnut'>,
  ];

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const defaultData = {
    labels: ['Carregando...'],
    datasets: [
      {
        data: [1],
        backgroundColor: ['#f0f0f0'],
        cutout: '65%',
      },
    ],
  };

  const truncateLabel = (text: string, maxLength: number = 10) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const data = eventosFavoritos
    ? {
        labels: eventosFavoritos.eventos.map((evento) => evento.name),
        datasets: [
          {
            data: eventosFavoritos.eventos.map((evento) => evento.count),
            backgroundColor: [
              '#b3c8f2',
              '#dbe4f9',
              '#2c3e50',
              '#6a89cc',
              '#4a69bd',
              '#1e3799',
            ].slice(0, eventosFavoritos.eventos.length),
            borderColor: '#ffffff',
            borderWidth: 0,
            cutout: '65%',
          },
        ],
      }
    : defaultData;

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          // tooltip sempre mostra o nome completo
          label: (context) => {
            const label = context.chart.data.labels?.[context.dataIndex] || '';
            const value = context.formattedValue;
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: true,
        formatter: (value, context) => {
          if (!eventosFavoritos) return 'Carregando...';
          const label = context.chart.data.labels?.[context.dataIndex] as string;
          // aqui sim aplicamos o truncate para o texto que fica no gráfico
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          return `${truncateLabel(label)}\n${value}`;
        },
        color: '#333333',
        font: {
          size: 12,
          weight: 'bold' as const,
        },
        anchor: 'end',
        align: 'end',
        offset: 30,
        textAlign: 'center',
      },
    },
  };
  const defaultStyle: React.CSSProperties = {
    maxWidth: '584px',
    minWidth: 304,
    maxHeight: 500,
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
  };

  if (isLoading || !eventosFavoritos) {
    return (
      <div
        style={{
          width: '584px',
          padding: '20px',
          backgroundColor: '#ececec',
          borderRadius: '8px',
          maxHeight: 540,
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          ...style, // ✅ aplica style também no estado de loading
        }}
      >
        <h3 style={{ color: '#5A5A5A', marginBottom: '20px', fontSize: 16 }}>Eventos favoritos</h3>
        <div>Carregando dados...</div>
      </div>
    );
  }

  return (
    <div style={{ ...defaultStyle, ...style }}>
      <h3 style={{ color: '#5A5A5A', marginBottom: '20px', fontSize: 16 }}>Eventos favoritos</h3>
      <div style={{ height: '400px' }}>
        <Doughnut
          data={data}
          options={{
            ...options,
            layout: {
              padding: { left: 20, right: 20, top: 70, bottom: 70 },
            },
          }}
          plugins={plugins}
        />
      </div>
      <p style={{ textAlign: 'right', marginTop: '10px', color: '#4a4a4a', fontWeight: 'bold' }}>
        Total: <span style={{ color: '#6a89cc' }}>{eventosFavoritos.total}</span>
      </p>
    </div>
  );
};

export default GraficoEventosFavoritos;
